/**
 * OAuth Popup Utility
 * Opens a popup window for OAuth authentication and handles the callback.
 */

export interface OAuthPopupResult {
  success: boolean;
  error?: string;
  connectionId?: string;
}

export interface OAuthPopupOptions {
  /** The authorization URL to open */
  url: string;
  /** Popup window title (default: 'Connect Calendar') */
  title?: string;
  /** Popup width in pixels (default: 600) */
  width?: number;
  /** Popup height in pixels (default: 700) */
  height?: number;
  /** Timeout in milliseconds (default: 5 minutes) */
  timeout?: number;
}

interface OAuthCallbackMessage {
  type: 'OAUTH_CALLBACK';
  success: boolean;
  connectionId?: string;
  error?: string;
}

function isOAuthCallbackMessage(data: unknown): data is OAuthCallbackMessage {
  return (
    typeof data === 'object' &&
    data !== null &&
    'type' in data &&
    (data as OAuthCallbackMessage).type === 'OAUTH_CALLBACK'
  );
}

/**
 * Opens a centered popup window for OAuth authentication.
 * Listens for postMessage from the popup containing the result.
 *
 * @example
 * ```tsx
 * const result = await openOAuthPopup({
 *   url: 'https://accounts.google.com/o/oauth2/...',
 * });
 *
 * if (result.success) {
 *   console.log('Connected:', result.connectionId);
 * } else {
 *   console.error('Failed:', result.error);
 * }
 * ```
 */
export function openOAuthPopup(options: OAuthPopupOptions): Promise<OAuthPopupResult> {
  const {
    url,
    title = 'Connect Calendar',
    width = 600,
    height = 700,
    timeout = 5 * 60 * 1000, // 5 minutes
  } = options;

  return new Promise((resolve) => {
    // Calculate centered position
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;

    // Open popup
    const popup = window.open(
      url,
      title,
      `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no,location=no,status=no`
    );

    if (!popup) {
      resolve({
        success: false,
        error: 'Popup was blocked. Please allow popups for this site.',
      });
      return;
    }

    let resolved = false;

    // Handle message from popup
    const handleMessage = (event: MessageEvent) => {
      // Verify origin matches our app
      if (event.origin !== window.location.origin) {
        return;
      }

      if (isOAuthCallbackMessage(event.data)) {
        resolved = true;
        cleanup();
        resolve({
          success: event.data.success,
          connectionId: event.data.connectionId,
          error: event.data.error,
        });
      }
    };

    // Poll for popup close (user closed without completing)
    const pollInterval = setInterval(() => {
      if (popup.closed && !resolved) {
        resolved = true;
        cleanup();
        resolve({
          success: false,
          error: 'Authorization was cancelled',
        });
      }
    }, 500);

    // Timeout
    const timeoutId = setTimeout(() => {
      if (!resolved) {
        resolved = true;
        cleanup();
        popup.close();
        resolve({
          success: false,
          error: 'Authorization timed out. Please try again.',
        });
      }
    }, timeout);

    // Cleanup function
    const cleanup = () => {
      window.removeEventListener('message', handleMessage);
      clearInterval(pollInterval);
      clearTimeout(timeoutId);
    };

    window.addEventListener('message', handleMessage);
  });
}
